import {inject, Injectable} from '@angular/core';
import {HttpClient, HttpErrorResponse} from '@angular/common/http';
import {Observable, RetryConfig, retry, throwError, timer} from 'rxjs';
import {GeoLookupModel} from '@wellfit/wf-plans-services';
import {environment} from 'src/environments/environment';
import {GetMembershipAgreementRequest} from '../models/document.request';
import {EventsManager} from '../events/events.manager';
import {MainPageDetailModel} from '../models/plans.model';
import {LegalModel} from '../models/legal.model';
import {ensureTrailingSlash} from '../utils/url.util';

@Injectable({providedIn: 'root'})

export class PlansService {
  private static readonly OFFICE_RETRY_COUNT: number = 2;
  private static readonly OFFICE_RETRY_BASE_DELAY_MS: number = 300;

  private static readonly OFFICE_RETRY: RetryConfig = {
    count: PlansService.OFFICE_RETRY_COUNT,
    delay: (error: unknown, retryCount: number): Observable<number> => {
      if (!PlansService.isTransientError(error)) {
        return throwError((): unknown => error);
      }
      return timer(retryCount * PlansService.OFFICE_RETRY_BASE_DELAY_MS);
    }
  };

  private readonly _eventsManager: EventsManager = inject(EventsManager);
  private readonly _httpClient: HttpClient = inject(HttpClient);

  public getOffice(office: string): Observable<MainPageDetailModel> {
    const url: string = `${ensureTrailingSlash(environment.widgetApi)}npm/get-main-page-detail`;
    return this._httpClient.get<MainPageDetailModel>(url + '?OrganizationId=' + this._eventsManager.orgId + '&PracticeId=' + office)
      .pipe(retry(PlansService.OFFICE_RETRY));
  }

  public getLegalLanguage(): Observable<LegalModel[]> {
    return this._httpClient.get<LegalModel[]>('data/language.json');
  }

  public getOfficeByVanityName(orgVanity: string, practiceVanity: string): Observable<MainPageDetailModel> {
    const url: string = `${environment.apiUrl}sales/purchase/portal/get-office-by-vanity?orgVanity=${orgVanity}&practiceVanity=${practiceVanity}`;
    return this._httpClient.get<MainPageDetailModel>(url).pipe(retry(PlansService.OFFICE_RETRY));
  }

  // Called by the package's PracticeSelectionFormComponent via the
  // {provide: PracticePlansService, useClass: PlansService} override in
  // app.module — routes the zip search to the legacy Sales API instead of
  // the plans-widget default. Signature must match PracticePlansService.getOffices.
  public getOffices(lookup: GeoLookupModel): Observable<any> {
    const url: string = `${environment.apiUrl}sales/purchase/portal/get-practices-by-zipcode`;
    return this._httpClient.get(`${url}?ZipCode=${lookup.zipCode}&distance=${lookup.dist}&organizationId=${lookup.org}`);
  }

  public getMembershipAgreement(request: GetMembershipAgreementRequest, useMembershipAgreementTemplate?: boolean): Observable<Blob > {
    let requestUrl: string = `${environment.pdfUrl}print/plan-member-agreement?PracticeName=${request.practiceName}&PlanName=${request.planName}`;
    if (useMembershipAgreementTemplate) {
      requestUrl += '&useMembershipAgreementTemplate=true';
    }
    return this._httpClient.get(requestUrl, {responseType: 'blob'});
  }

  private static isTransientError(error: unknown): boolean {
    return error instanceof HttpErrorResponse && (error.status === 0 || error.status >= 500);
  }
}
